/**
 * Centralized Permission & Role Manager Helper
 */
import { PERMISSIONS, ROLES, ROLE_DISPLAY_NAMES } from "../constants/permissions";

/**
 * Check if user is Super Admin
 */
export const isSuperAdmin = (user) => {
  if (!user) return false;
  return (
    user.user_type === ROLES.SUPER_ADMIN ||
    user.role_key === ROLES.SUPER_ADMIN ||
    (Array.isArray(user.permissions) && user.permissions.includes("*"))
  );
};

/**
 * Check if user is Vendor
 */
export const isVendor = (user) => {
  if (!user) return false;
  return user.user_type === ROLES.VENDOR || user.role_key === ROLES.VENDOR;
};

/**
 * Check if user is Admin
 */
export const isAdmin = (user) => {
  if (!user) return false;
  return (
    user.user_type === ROLES.ADMIN ||
    user.user_type === ROLES.SUPER_ADMIN ||
    user.role_key === ROLES.SUPER_ADMIN
  );
};

/**
 * Check if user has a single permission or any of multiple permissions
 * @param {Object} user - User object from auth state
 * @param {string|string[]} permission - Single permission constant or array of permissions
 * @returns {boolean}
 */
export const hasPermission = (user, permission) => {
  if (!user) return false;

  // Super Admin bypass
  if (isSuperAdmin(user)) return true;

  const userPermissions = Array.isArray(user.permissions) ? user.permissions : [];

  if (Array.isArray(permission)) {
    return permission.some((p) => userPermissions.includes(p));
  }

  return userPermissions.includes(permission);
};

/**
 * Check if user has ALL specified permissions
 * @param {Object} user - User object from auth state
 * @param {string[]} permissions - Array of permission constants
 * @returns {boolean}
 */
export const hasAllPermissions = (user, permissions) => {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;
  if (!Array.isArray(permissions) || permissions.length === 0) return true;

  const userPermissions = Array.isArray(user.permissions) ? user.permissions : [];
  return permissions.every((p) => userPermissions.includes(p));
};

/**
 * Get formatted role title for user
 * @param {Object} user - User object
 * @returns {string}
 */
export const getUserRoleTitle = (user) => {
  if (!user) return "User";
  if (user.role) return user.role;
  if (user.role_name) return user.role_name;
  if (user.role_key && ROLE_DISPLAY_NAMES[user.role_key]) {
    return ROLE_DISPLAY_NAMES[user.role_key];
  }
  if (user.user_type && ROLE_DISPLAY_NAMES[user.user_type]) {
    return ROLE_DISPLAY_NAMES[user.user_type];
  }
  return user.user_type || "User";
};

export { PERMISSIONS, ROLES, ROLE_DISPLAY_NAMES };

export default {
  hasPermission,
  hasAllPermissions,
  isSuperAdmin,
  isVendor,
  isAdmin,
  getUserRoleTitle,
  PERMISSIONS,
  ROLES,
  ROLE_DISPLAY_NAMES,
};
